import React from "react";
import { useDeepCompareEffect } from "ahooks";
import { debounce, uniqBy } from "lodash";
import qs from "qs";
import { useCallback, useEffect, useRef, useState } from "react";
import LazySelect from "./lazy-select";
import axios from "axios";
import { useStateCallback } from "../../hooks/useStateCallback";

export default function ServerPaginatedSelect({
  url,
  pageSize = 20, // 0 means no pagination
  renderItem,
  valueResolver = (item) => item?.id,

  style,
  onChange,
  value,
  dataSource,
  searchDebouncing = 500,
  fetchDefaultValue = (id) =>
    axios
      .get(`${url.split("?")[0]}/${id}`)
      .then((res) => res.data)
      .catch(console.error),
  ...props
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [noMore, setNoMore] = useStateCallback(false);
  const currPage = useRef(1);
  const [opened, setOpened] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const _fetchData = useRef(() => {});

  const fetchData = () => {
    if (noMore) return;

    setLoading(true);
    const [_url, query = ""] = url.split("?");

    const filters = qs.parse(query) ?? {};
    if (pageSize) {
      filters.limit = pageSize;
      filters.page = currPage.current++;
    }
    if (searchTerm) filters.name = searchTerm;

    axios
      .get(`${_url}?${qs.stringify(filters)}`)
      .then(({ data: { data } }) => {
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data.list)
          ? data.list
          : [];

        if (!pageSize || list.length < pageSize) setNoMore(true);

        setData((prev) => uniqBy(prev.concat(list), "id"));
      })
      // @ts-ignore
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    _fetchData.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    if (!opened) return;
    setData([]);
    currPage.current = 1;
    setNoMore(false, () => setTimeout(() => _fetchData.current(), 100));
  }, [opened]);

  const search = useCallback(
    debounce(() => {
      if (!opened) return;
      setData([]);
      currPage.current = 1;
      setNoMore(false, () => setTimeout(() => _fetchData.current(), 100));
    }, searchDebouncing),
    [searchDebouncing, opened]
  );

  const onSearch = useCallback(
    (value) => {
      if (value.length > 1 && value.length < 3) return;
      setSearchTerm(value);
      if (pageSize) search();
    },
    [search]
  );

  useDeepCompareEffect(() => {
    const val = props?.defaultValue ?? value;

    if (
      val &&
      !data.find((item) => valueResolver(item) === val) &&
      fetchDefaultValue
    ) {
      fetchDefaultValue(val)
        .then((item) =>
          setData((prev) => uniqBy(prev.concat(item), "id"))
        )
        .catch(console.error);
    }
  }, [props?.defaultValue]);

  const _data = dataSource ? dataSource(data) : data;

  return (
    <LazySelect
      onChange={(value) => {
        onChange && onChange(
          value,
          _data.find((item) => valueResolver(item) === value)
        );
      }}
      onEndReached={() => {
        _fetchData.current();
      }}
      loading={loading}
      dataSource={_data}
      // @ts-ignore
      renderItem={renderItem}
      valueResolver={valueResolver}
      style={style}
      skeletonEntriesCount={3}
      onDropdownVisibleChange={(open) => setOpened(open)}
      showSearch
      allowClear
      filterOption={!pageSize}
      onSearch={onSearch}
      value={value}
      optionFilterProp={pageSize ? undefined : "node"}
      {...props}
    />
  );
}
